import { ApiClient } from '@api/api-configuration';
import { type InventoryCategory } from '@api/READ_ONLY/services_api/Api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

const queryKey = ['all-inventory-categories-limit-1000'];

const useGlobalInventoryCategories = () => {
  const queryClient = useQueryClient();

  const {
    data: res,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () =>
      ApiClient.serviceApi.inventory.listInventoryCategories({ limit: 1000, offset: 0, sort: 'name' }),
    staleTime: 5 * 60 * 1000,
  });

  // Memoised so the `?? []` fallback does not hand out a fresh array — and so a
  // fresh getCategoryName — on every render.
  const categories: InventoryCategory[] = useMemo(() => res?.data?.data ?? [], [res]);

  const invalidateGlobalInventoryCategories = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient]);

  // Name lookup for the items table, which receives only categoryId from the API.
  const getCategoryName = useCallback(
    (id: number | null | undefined): string | undefined =>
      id == null ? undefined : categories.find((c) => c.categoryId === id)?.name,
    [categories]
  );

  return {
    globalInventoryCategories: categories,
    areGlobalInventoryCategoriesLoading: isLoading,
    areGlobalInventoryCategoriesFetching: isFetching,
    hasGlobalInventoryCategoriesError: isError,
    globalInventoryCategoriesError: error,
    invalidateGlobalInventoryCategories,
    getCategoryName,
  };
};

export default useGlobalInventoryCategories;
